import React from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { useAppSelector } from "../../../redux/store";
import { reportSliceSelectors } from "../../../redux/report/report.selector";
import { ActivityEntityKind } from "../../../model/activity/types/ActivityEntityKind.enum";
import { useTheme } from "../../../ThemeContext";
import { getKindColor } from "../../../utils/Reports";

const Frame = styled(Flex)`
  width: 100%;
  height: 16rem;
`;

const SERIES: ActivityEntityKind[] = [
  ActivityEntityKind.ENTRY,
  ActivityEntityKind.KANBAN_CARD,
  ActivityEntityKind.TASK,
];

const seriesLabelKey = (kind: ActivityEntityKind) => {
  switch (kind) {
    case ActivityEntityKind.KANBAN_CARD:
      return "common.deals";
    case ActivityEntityKind.TASK:
      return "common.tasks";
    default:
      return "common.entries";
  }
};

/**
 * New records per day, across the three entities the report already tracks
 * creation for, on one chart. Each entity tab has its own single-kind trend
 * chart further down; this is the one place that answers "is inflow overall
 * up or down" without switching tabs to check each one separately.
 */
export const ReportInsertionTrendChart: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const trend = useAppSelector(reportSliceSelectors.selectReport)?.trend || [];

  if (
    trend.length < 2 ||
    SERIES.every((kind) => trend.every((point) => !point[kind]))
  ) {
    return null;
  }

  return (
    <Flex flexDirection="column" gap="xs">
      <Text fontWeight="bold">{t("reports.insertion.title")}</Text>
      <Frame>
        <ResponsiveContainer>
          <LineChart data={trend} margin={{ top: 8, right: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11 }}
              tickFormatter={(day: string) => day.slice(5)}
              minTickGap={16}
            />
            <YAxis allowDecimals={false} width={28} tick={{ fontSize: 11 }} />
            <Tooltip
              wrapperStyle={{ zIndex: 999999 }}
              labelFormatter={(day) =>
                dayjs(String(day), "YYYY-MM-DD").format("DD MMM YYYY")
              }
            />
            <Legend />
            {SERIES.map((kind) => (
              <Line
                key={kind}
                type="monotone"
                dataKey={kind}
                name={t(seriesLabelKey(kind))}
                stroke={getKindColor(kind, theme)}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Frame>
    </Flex>
  );
};
